import type { Difficulty, GameMode, Move, Side } from './types';
import type { StoredGame } from './storage';

const IDENTITY_KEY = 'cloud-xiangqi:online-player:v1';
const SUBMITTED_KEY = 'cloud-xiangqi:online-submitted:v1';
const MATCH_TICKETS_KEY = 'cloud-xiangqi:online-match-tickets:v1';

export interface OnlineIdentity {
  playerId: string;
  token: string;
  displayName: string;
}

export interface LeaderboardPlayer {
  id: string;
  displayName: string;
  overallScore: number;
  storyReputation: number;
  storyScore: number;
  freeRating: number;
  freeGames: number;
  freeWins: number;
  freeDraws: number;
  freeLosses: number;
  storyGames: number;
  storyWins: number;
  storyDraws: number;
  storyLosses: number;
  clears: number;
  hasPassword?: boolean;
  storyProgress?: Partial<Record<Difficulty, number>>;
}

export interface LeaderboardSnapshot {
  me: LeaderboardPlayer | null;
  leaderboards: {
    overall: LeaderboardPlayer[];
    story: LeaderboardPlayer[];
    free: LeaderboardPlayer[];
  };
}

export interface SubmittedScore {
  accepted: boolean;
  duplicate: boolean;
  delta: number;
  player: LeaderboardPlayer;
}

export interface PortableOnlineMatchTicket {
  gameId: string;
  ticketId: string;
  secret: string;
  expiresAt: string;
  playerId?: string;
}

export interface CloudGameSave {
  game: StoredGame;
  updatedAt: string;
  onlineMatchTicket?: PortableOnlineMatchTicket;
}

const pendingMatchStarts = new Map<string, Promise<PortableOnlineMatchTicket | null>>();

function randomSecret(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
}

export function loadOnlineIdentity(): OnlineIdentity | null {
  try {
    const value = JSON.parse(localStorage.getItem(IDENTITY_KEY) || 'null') as Partial<OnlineIdentity> | null;
    return value?.playerId && value.token && value.displayName
      ? { playerId: value.playerId, token: value.token, displayName: value.displayName }
      : null;
  } catch {
    return null;
  }
}

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
  });
  const data = await response.json().catch(() => ({})) as T & { error?: string };
  if (!response.ok) throw new Error(data.error || '排行榜服務暫時無法使用');
  return data;
}

function saveIdentity(identity: OnlineIdentity): void {
  localStorage.setItem(IDENTITY_KEY, JSON.stringify(identity));
}

export async function registerOnlinePlayer(displayName: string, password: string): Promise<{ identity: OnlineIdentity; player: LeaderboardPlayer }> {
  const identity: OnlineIdentity = {
    playerId: crypto.randomUUID(),
    token: randomSecret(),
    displayName: '',
  };
  const result = await api<{ player: LeaderboardPlayer }>('/api/leaderboard', {
    method: 'POST',
    body: JSON.stringify({ action: 'register', playerId: identity.playerId, token: identity.token, displayName, password }),
  });
  identity.displayName = result.player.displayName;
  saveIdentity(identity);
  return { identity, player: result.player };
}

export async function loginOnlinePlayer(displayName: string, password: string): Promise<{ identity: OnlineIdentity; player: LeaderboardPlayer }> {
  const token = randomSecret();
  const result = await api<{ player: LeaderboardPlayer }>('/api/leaderboard', {
    method: 'POST',
    body: JSON.stringify({ action: 'login', displayName, password, token }),
  });
  const identity = { playerId: result.player.id, token, displayName: result.player.displayName };
  saveIdentity(identity);
  return { identity, player: result.player };
}

export async function renameOnlinePlayer(displayName: string): Promise<{ identity: OnlineIdentity; player: LeaderboardPlayer }> {
  const identity = loadOnlineIdentity();
  if (!identity) throw new Error('請先登入棋士帳號');
  const result = await api<{ player: LeaderboardPlayer }>('/api/leaderboard', {
    method: 'POST',
    body: JSON.stringify({ action: 'register', playerId: identity.playerId, token: identity.token, displayName }),
  });
  identity.displayName = result.player.displayName;
  saveIdentity(identity);
  return { identity, player: result.player };
}

export async function setOnlinePassword(password: string, currentPassword = ''): Promise<LeaderboardPlayer> {
  const identity = loadOnlineIdentity();
  if (!identity) throw new Error('請先登入棋士帳號');
  const result = await api<{ player: LeaderboardPlayer }>('/api/leaderboard', {
    method: 'POST',
    body: JSON.stringify({ action: 'set_password', playerId: identity.playerId, token: identity.token, password, currentPassword }),
  });
  return result.player;
}

export async function fetchLeaderboard(identity = loadOnlineIdentity()): Promise<LeaderboardSnapshot> {
  return api<LeaderboardSnapshot>('/api/leaderboard', identity ? {
    headers: { 'X-Player-Id': identity.playerId, Authorization: `Bearer ${identity.token}` },
  } : undefined);
}

export async function saveCloudGame(
  game: StoredGame,
  identity = loadOnlineIdentity(),
  keepalive = false,
): Promise<boolean> {
  if (!identity) return false;
  const result = await api<{ saved: boolean }>('/api/leaderboard', {
    method: 'POST',
    keepalive,
    body: JSON.stringify({ action: 'cloud_save', playerId: identity.playerId, token: identity.token, game }),
  });
  return result.saved;
}

export async function loadCloudGame(identity = loadOnlineIdentity()): Promise<CloudGameSave | null> {
  if (!identity) return null;
  const result = await api<{ save: CloudGameSave | null }>('/api/leaderboard', {
    method: 'POST',
    body: JSON.stringify({ action: 'cloud_load', playerId: identity.playerId, token: identity.token }),
  });
  return result.save;
}

export async function deleteCloudGame(identity = loadOnlineIdentity()): Promise<boolean> {
  if (!identity) return false;
  const result = await api<{ deleted: boolean }>('/api/leaderboard', {
    method: 'POST',
    body: JSON.stringify({ action: 'cloud_delete', playerId: identity.playerId, token: identity.token }),
  });
  return result.deleted;
}

function submittedGameIds(): string[] {
  try {
    const value = JSON.parse(localStorage.getItem(SUBMITTED_KEY) || '[]') as unknown;
    return Array.isArray(value) ? value.map(String).slice(-200) : [];
  } catch {
    return [];
  }
}

function parseMatchTicket(entry: unknown): PortableOnlineMatchTicket | null {
  if (!entry || typeof entry !== 'object') return null;
  const value = entry as Partial<PortableOnlineMatchTicket>;
  if (typeof value.gameId !== 'string' || typeof value.ticketId !== 'string'
    || typeof value.secret !== 'string' || value.secret.length < 32
    || typeof value.expiresAt !== 'string' || Date.parse(value.expiresAt) <= Date.now()
    || (value.playerId !== undefined && typeof value.playerId !== 'string')) return null;
  return {
    gameId: value.gameId,
    ticketId: value.ticketId,
    secret: value.secret,
    expiresAt: value.expiresAt,
    ...(value.playerId ? { playerId: value.playerId } : {}),
  };
}

function matchTickets(): PortableOnlineMatchTicket[] {
  try {
    const value = JSON.parse(localStorage.getItem(MATCH_TICKETS_KEY) || '[]') as unknown;
    if (!Array.isArray(value)) return [];
    return value.map(parseMatchTicket).filter((entry): entry is PortableOnlineMatchTicket => Boolean(entry)).slice(-50);
  } catch {
    return [];
  }
}

function saveMatchTickets(tickets: PortableOnlineMatchTicket[]): void {
  localStorage.setItem(MATCH_TICKETS_KEY, JSON.stringify(tickets.slice(-50)));
}

export function hasOnlineMatchTicket(gameId: string, identity = loadOnlineIdentity()): boolean {
  return matchTickets().some((ticket) => ticket.gameId === gameId
    && (!ticket.playerId || ticket.playerId === identity?.playerId));
}

export function portableOnlineMatchTicket(
  gameId: string,
  identity = loadOnlineIdentity(),
): PortableOnlineMatchTicket | null {
  if (!identity) return null;
  const ticket = matchTickets().find((entry) => entry.gameId === gameId
    && (!entry.playerId || entry.playerId === identity.playerId));
  return ticket ? { ...ticket, playerId: identity.playerId } : null;
}

export function importPortableOnlineMatchTicket(
  input: unknown,
  expectedGameId: string,
  identity = loadOnlineIdentity(),
): boolean {
  const ticket = parseMatchTicket(input);
  if (!ticket || ticket.gameId !== expectedGameId) return false;
  if (identity && ticket.playerId && ticket.playerId !== identity.playerId) return false;
  const imported = { ...ticket, ...(identity ? { playerId: identity.playerId } : {}) };
  saveMatchTickets([...matchTickets().filter((entry) => entry.gameId !== expectedGameId), imported]);
  return true;
}

export async function beginOnlineMatch(input: {
  gameId: string;
  mode: GameMode;
  difficulty: Difficulty;
  playerSide: Side;
}): Promise<boolean> {
  const identity = loadOnlineIdentity();
  if (!identity || submittedGameIds().includes(input.gameId)) return false;
  if (matchTickets().some((ticket) => ticket.gameId === input.gameId
    && (!ticket.playerId || ticket.playerId === identity.playerId))) return true;
  const existing = pendingMatchStarts.get(input.gameId);
  if (existing) return Boolean(await existing);
  const pending = api<Omit<PortableOnlineMatchTicket, 'gameId' | 'playerId'>>('/api/leaderboard', {
    method: 'POST',
    body: JSON.stringify({ action: 'begin_match', playerId: identity.playerId, token: identity.token, ...input }),
  }).then((result) => {
    const ticket = { gameId: input.gameId, playerId: identity.playerId, ...result };
    saveMatchTickets([...matchTickets().filter((entry) => entry.gameId !== input.gameId), ticket]);
    return ticket;
  }).finally(() => pendingMatchStarts.delete(input.gameId));
  pendingMatchStarts.set(input.gameId, pending);
  return Boolean(await pending);
}

export async function submitOnlineResult(input: {
  gameId: string;
  mode: GameMode;
  difficulty: Difficulty;
  result: 'win' | 'draw' | 'loss';
  moveTrace: Move[];
  undosUsed: number;
  playerSide: Side;
  resigned: boolean;
}): Promise<SubmittedScore | null> {
  const identity = loadOnlineIdentity();
  if (!identity || submittedGameIds().includes(input.gameId)) return null;
  await pendingMatchStarts.get(input.gameId);
  const ticket = matchTickets().find((entry) => entry.gameId === input.gameId
    && (!entry.playerId || entry.playerId === identity.playerId));
  if (!ticket) throw new Error('本局開始時未取得線上登記憑證，成績不會送入排行榜');
  const result = await api<SubmittedScore>('/api/leaderboard', {
    method: 'POST',
    body: JSON.stringify({
      action: 'result', playerId: identity.playerId, token: identity.token,
      ticketId: ticket.ticketId, ticketSecret: ticket.secret, ...input,
    }),
  });
  localStorage.setItem(SUBMITTED_KEY, JSON.stringify([...submittedGameIds(), input.gameId].slice(-200)));
  saveMatchTickets(matchTickets().filter((entry) => entry.gameId !== input.gameId));
  return result;
}
